
















































































































































































import { Vue, Component, Mixins, Ref } from "vue-property-decorator";

import AppContent from "@/components/AppContent/AppContent.vue";
import AppHeaderBackWithTitle from "@/components/AppHeader/AppHeaderBackWithTitle.vue";
import AppHeader from "@/components/AppHeader/AppHeader.vue";
import Spinner from "@/components/BaseSpinner/BaseSpinner.vue";
import Alert from "@/components/BaseAlert/BaseAlert.vue";
import VueHtml2pdf from "vue-html2pdf";
import BaseButton from "@/components/BaseButton/BaseButton.vue";
import StickyListHeader from "@/components/StickyListHeader/StickyListHeader.vue";

import LessonReport from "./Reports/LessonReport.vue";
import LevelReport from "./Reports/LevelReport.vue";
import UnitReport from "./Reports/UnitReport.vue";
import LevelUnitReportIndividual from "./Reports/LevelUnitReportIndividual.vue";
import TestReport from "./Reports/TestReport.vue";
import DiagnosticTestReport from "./Reports/DiagnosticTestReport.vue";
import ProgressReport from "./Reports/ProgressReport.vue";

import { Dictionary } from "vue-router/types/router";
import { State, Action } from "vuex-class";

import { IVuexState } from "@/models/interfaces/store";
import { ITestCategory, ISchool } from "@/models/interfaces/schools";
import { ReportTypes } from "@/models/enums/report-generator";
import { IDropdownItem } from "@/models/interfaces/dropdown";
import { IStudent } from "@/models/interfaces/students";
import {
  ITestReportItem,
  IDiagnosticReportItem,
  ILessonReport,
  ILevelReport,
  IReportParams,
  ITestReport,
  IUnitReport,
  IProgressReportItem
} from "@/models/interfaces/report-generator";
import RequestState from "@/utils/mixins/RequestState";

import { ILesson } from "@/models/interfaces/lessons";
import { IClass } from "@/models/interfaces/classes";
import { AxiosResponse } from "axios";
import amplitude from "amplitude-js";
import { eventCodeByName, EventsNames } from "@/analytics";

interface PdfComponent extends HTMLElement {
  generatePdf: () => void;
}

interface ReportComponent extends HTMLElement {
  generateCsvFile: () => void;
  toggleSortable: () => void;
}

@Component({
  components: {
    AppHeaderBackWithTitle,
    AppHeader,
    AppContent,
    Spinner,
    Alert,
    BaseButton,
    StickyListHeader,

    LessonReport,
    LevelReport,
    UnitReport,
    LevelUnitReportIndividual,
    TestReport,
    DiagnosticTestReport,
    ProgressReport,

    VueHtml2pdf
  }
})
export default class ReportGeneratorResult extends Mixins(RequestState) {
  @Ref("toPdf")
  private pdfContent!: PdfComponent;

  @Ref("lessonReportRef")
  private lessonReportRef!: Vue & ReportComponent;

  @Ref("levelReportRef")
  private levelReportRef!: Vue & ReportComponent;

  @Ref("levelInvividualReportRef")
  private levelUnitInvividualReportRef!: Vue & ReportComponent;

  @Ref("unitReportRef")
  private unitReportRef!: Vue & ReportComponent;

  @Ref("testReportRef")
  private testReportRef!: Vue & ReportComponent;

  @Ref("progressReportRef")
  private progressReportRef!: Vue & ReportComponent;

  @Ref("diagnosticTestReportRef")
  private diagnosticTestReportRef!: Vue & ReportComponent;

  @State("schools", { namespace: "schools" })
  private schools!: IVuexState<ISchool[]>;

  @State("classes", { namespace: "classes" })
  private classes!: IVuexState<IClass[]>;

  @State("students", { namespace: "classes" })
  private students!: IVuexState<IStudent[]>;

  @State("student", { namespace: "students" })
  private student!: IVuexState<IStudent>;

  @State("report", { namespace: "reports" })
  private report!: IVuexState<ILessonReport | ILevelReport>;

  @State("lessons", { namespace: "lessons" })
  private lessons!: IVuexState<ILesson[]>;

  @State("testCategories", { namespace: "schools" })
  private testCategories!: IVuexState<ITestCategory[]>;

  @Action("schools/fetchSchools")
  private fetchSchools!: () => Promise<{ schools: ISchool[] }>;

  @Action("classes/fetchClassesForSchool")
  private fetchClassesForSchool!: (ids: string[]) => Promise<IClass[]>;

  @Action("classes/fetchStudentsInClass")
  private fetchStudentsInClass!: (classId: string) => Promise<IStudent[]>;

  @Action("reports/generateLessonReport")
  private generateLessonReport!: (
    params: IReportParams
  ) => Promise<ILessonReport>;

  @Action("students/fetchStudentById")
  private fetchStudentById!: (studentId: string) => Promise<AxiosResponse>;

  @Action("reports/generateLevelReport")
  private generateLevelReport!: (
    params: IReportParams
  ) => Promise<ILevelReport>;

  @Action("reports/generateIndividualReport")
  private generateIndividualReport!: (
    params: IReportParams
  ) => Promise<ILevelReport>;

  @Action("reports/generateUnitReport")
  private generateUnitReport!: (params: IReportParams) => Promise<IUnitReport>;

  @Action("reports/generateTestReport")
  private generateTestReport!: (
    params: IReportParams
  ) => Promise<ITestReport["userExams"]>;

  @Action("reports/generateDiagnosticTestReport")
  private generateDiagnosticTestReport!: (
    params: IReportParams
  ) => Promise<IDiagnosticReportItem[]>;

  @Action("reports/generateBaselineTestReport")
  private generateBaselineTestReport!: (
    params: IReportParams
  ) => Promise<ITestReportItem[]>;

  @Action("reports/generateProgressReport")
  private generateProgressReport!: (
    params: IReportParams
  ) => Promise<IProgressReportItem[]>;

  @Action("schools/fetchTestCategories")
  private fetchTestCategories!: (payload: {
    type: ReportTypes;
  }) => Promise<ITestCategory[]>;

  private loading = true;
  private showToolbar = true;

  private reportTypes = ReportTypes;

  private get showIndividualReport() {
    return (
      this.reportType === this.reportTypes.LevelIndividual ||
      this.reportType === this.reportTypes.UnitIndividual
    );
  }

  private get lesson() {
    const report = this.report.data as ILessonReport;
    return report?.lesson;
  }

  private get reportType() {
    const rootType = this.$route.query.reportType.toString();
    return this.isIndividual ? `${rootType}Individual` : rootType;
  }

  private get reportTypeLabel() {
    const { query } = this.$route;

    const levelValue =
      (this.$t("tables.reports.levelNumber") as string) + ` ${query.level}`;
    const unitValue =
      (((this.$t("tables.reports.levelNumber") as string) +
        ` ${query.level},` +
        this.$t("tables.reports.topic")) as string) + ` ${query.unit}`;

    switch (this.reportType) {
      case ReportTypes.Lesson:
        return this.lesson ? this.lesson!.title : "";
      case ReportTypes.Level:
        return levelValue;
      case ReportTypes.LevelIndividual:
        return levelValue;
      case ReportTypes.Unit:
        return unitValue;
      case ReportTypes.UnitIndividual:
        return unitValue;
      case ReportTypes.AssessmentReport:
        return this.testCategories.data?.find(
          item => item.category === Number(query.examId)
        )?.description;
      case ReportTypes.DiagnosticTestReport:
        return this.$t("common.siteUsage") as string;
      case ReportTypes.BaselineTestReport:
        return this.$t("views.reports.baseline") as string;
      case ReportTypes.ProgressReport:
        return `${this.$t("views.reports.progress")}, ${levelValue}` as string;
      default:
        return "";
    }
  }

  private get getReportResultTitle() {
    const { schoolId, classId } = this.$route.query;

    const schoolName = this.schools.data!.find(
      item => item.schoolId === schoolId
    )!.schoolName;

    const className = classId
      ? this.classes.data!.find(item => item.classId === classId)!.className
      : "";

    const studentsPhrase =
      this.studentsCount === 0
        ? ""
        : this.studentsCount === 1
        ? `${this.student.data?.firstname ?? ""} ${this.student.data
            ?.lastname ?? ""}`
        : ((`${this.studentsCount} ` +
            this.$t("views.reports.students")) as string);

    const values = [
      schoolName,
      className,
      studentsPhrase,
      this.reportTypeLabel
    ];

    return values.filter(item => Boolean(item)).join(", ");
  }

  private get isIndividual() {
    const reportType = this.$route.query.reportType.toString();
    const levelOrUnit =
      reportType === this.reportTypes.Level ||
      reportType === this.reportTypes.Unit;

    return this.studentsCount === 1 && levelOrUnit;
  }

  private get reportsRefsObj(): Record<ReportTypes, ReportComponent> {
    return {
      [ReportTypes.Lesson]: this.lessonReportRef,
      [ReportTypes.Level]: this.levelReportRef,
      [ReportTypes.Unit]: this.unitReportRef,
      [ReportTypes.LevelIndividual]: this.levelUnitInvividualReportRef,
      [ReportTypes.UnitIndividual]: this.levelUnitInvividualReportRef,
      [ReportTypes.AssessmentReport]: this.testReportRef,
      [ReportTypes.DiagnosticTestReport]: this.diagnosticTestReportRef,
      [ReportTypes.BaselineTestReport]: this.testReportRef,
      [ReportTypes.ProgressReport]: this.progressReportRef
    };
  }

  private get studentsCount() {
    return Number(this.$route.query.studentCount);
  }

  private generatePdf() {
    let event = "";

    switch (this.reportType) {
      case "lessonReport":
        event = `${eventCodeByName(EventsNames.UserClicksPdfLessonReport)} ${
          EventsNames.UserClicksPdfLessonReport
        }`;
        break;
      case "levelReport" || "levelReportIndividual":
        event = `${eventCodeByName(EventsNames.UserClicksPdfLevelReport)} ${
          EventsNames.UserClicksPdfLevelReport
        }`;
        break;
      case "unitReport" || "unitReportIndividual":
        event = `${eventCodeByName(EventsNames.UserClicksPdfUnitReport)} ${
          EventsNames.UserClicksPdfUnitReport
        }`;
        break;
      default:
        break;
    }

    amplitude.getInstance().logEvent(event);
    // hide and than show the toolbar
    // so it will no be pasted into the pdf
    const toggleSortableArrows = this.reportsRefsObj[this.reportType]
      .toggleSortable;

    this.showToolbar = false;
    toggleSortableArrows();

    this.pdfContent.generatePdf();

    this.$nextTick(() => {
      this.showToolbar = true;
      toggleSortableArrows();
    });
  }

  private openFile(type: string) {
    if (type === "pdf") {
      amplitude
        .getInstance()
        .logEvent(
          `${eventCodeByName(
            EventsNames.UserClicksViewLessonPlanLessonReport
          )} ${EventsNames.UserClicksViewLessonPlanLessonReport}`
        );
    }

    window.open((this.report.data as ILessonReport).lesson[type]);
  }

  private getLabel(value: string, array: IDropdownItem[]) {
    return array.find(item => item.value === value)?.label;
  }

  private generateCsvFile() {
    let event = "";

    switch (this.reportType) {
      case "lessonReport":
        event = `${eventCodeByName(EventsNames.UserClicksCsvLessonReport)} ${
          EventsNames.UserClicksCsvLessonReport
        }`;
        break;
      case "levelReport" || "levelReportIndividual":
        event = `${eventCodeByName(EventsNames.UserClicksCsvLevelReport)} ${
          EventsNames.UserClicksCsvLevelReport
        }`;
        break;
      case "unitReport" || "unitReportIndividual":
        event = `${eventCodeByName(EventsNames.UserClicksCsvUnitReport)} ${
          EventsNames.UserClicksCsvUnitReport
        }`;
        break;
      default:
        break;
    }

    amplitude.getInstance().logEvent(event);
    const component = this.reportsRefsObj[this.reportType];

    component.generateCsvFile();
  }

  private parseReportParams(
    params: Record<string, string>
  ): Record<string, string[] | string> {
    let parsed = {};

    for (const param in params) {
      const value = String(params[param]) ?? "";

      if (value.includes("[") && value.includes("]")) {
        parsed[param] = JSON.parse(value);
      }
    }

    return {
      ...params,
      ...parsed
    };
  }

  private async created() {
    const { reportType, schoolId, level, unit, examId, ...otherParams } = this
      .$route.query as Dictionary<any>;
    const parsedParams = this.parseReportParams(otherParams);

    await Promise.all([
      this.fetchSchools(),
      this.fetchClassesForSchool([schoolId])
    ]);

    if (parsedParams.classId) {
      await this.fetchStudentsInClass(parsedParams.classId as string);
    }

    if (examId) {
      this.fetchTestCategories({
        type: reportType as ReportTypes
      });
    }

    switch (reportType) {
      case ReportTypes.Lesson:
        this.generateLessonReport({
          params: parsedParams,
          schoolId
        } as IReportParams);
        break;
      case ReportTypes.Level:
        this.studentsCount > 1 || parsedParams.classId
          ? this.generateLevelReport({
              params: parsedParams,
              schoolId,
              level
            } as IReportParams)
          : this.generateIndividualReport({
              params: { userId: parsedParams.userId as string[] },
              schoolId,
              level
            });
        break;
      case ReportTypes.Unit:
        this.studentsCount > 1 || parsedParams.classId
          ? this.generateUnitReport({
              params: parsedParams,
              schoolId,
              level,
              unit
            } as IReportParams)
          : this.generateIndividualReport({
              params: { unit, ...parsedParams },
              schoolId,
              level
            });
        break;
      case ReportTypes.AssessmentReport:
        this.generateTestReport({
          params: {
            ...parsedParams,
            level,
            category: examId
          },
          schoolId
        });
        break;
      case ReportTypes.DiagnosticTestReport:
        this.generateDiagnosticTestReport({
          params: parsedParams,
          schoolId
        });
        break;
      case ReportTypes.BaselineTestReport:
        this.generateBaselineTestReport({
          schoolId,
          params: {
            ...parsedParams,
            level,
            category: examId
          }
        });
        break;
      case ReportTypes.ProgressReport:
        this.generateProgressReport({
          schoolId,
          params: {
            ...parsedParams,
            level
          }
        });
        break;
      default:
        break;
    }

    this.loading = false;
  }
}
